import { gql } from '@apollo/client';

export const LOYALTY_CAMPAIGN_CONFIG_FRAGMENT = gql`
  fragment LoyaltyCampaignConfigFragment on LoyaltyCampaignConfig {
    id
    amount
    updatedAt
  }
`;

export const LOYALTY_CAMPAIGN_CARD_REWARD_FRAGMENT = gql`
  fragment LoyaltyCampaignCardRewardFragment on LevelReward {
    __typename
    ... on Currency {
      amount
    }
    ... on CurrencyBoost {
      amount
      boostType
    }
    ... on CurrencyUnlimitedBoost {
      amount
      unlimitedBoostType
    }
    ... on CurrencyCategoryFragment {
      amount
      category {
        id
      }
    }
    ... on CurrencyCategoryRandom {
      amount
    }
    ... on ContentPackReward {
      contentPack {
        id
      }
    }
    ... on AppSkin {
      id
    }
    ... on Sticker {
      id
    }
    ... on ProfileFrame {
      id
    }
    ... on Badge {
      id
      name
    }
    ... on Poster {
      id
    }
    ... on Emoji {
      id
    }
    ... on EmojiPack {
      id
    }
  }
`;

export const LOYALTY_CAMPAIGN_CARD_FRAGMENT = gql`
  fragment LoyaltyCampaignCardFragment on LoyaltyCampaignCard {
    id
    kind
    name
    groupId
    groupName
    groupDisplayOrder
    groupStartDate
    groupEndDate
    subgroupId
    subgroupName
    subgroupDisplayOrder
    subgroupStartDate
    subgroupEndDate
    itemDisplayOrder
    displayOrder
    cost
    itemBonusLoyaltyPoints
    maxClaims
    created
    rewards {
      ...LoyaltyCampaignCardRewardFragment
    }
  }
  ${LOYALTY_CAMPAIGN_CARD_REWARD_FRAGMENT}
`;

export const LOYALTY_CAMPAIGN_OFFER_OPTION_FRAGMENT = gql`
  fragment LoyaltyCampaignOfferOptionFragment on LoyaltyCampaignOfferOption {
    id
    label
    priceUsd
    skus
  }
`;

export const LOYALTY_CAMPAIGN_SKU_PRICE_FRAGMENT = gql`
  fragment LoyaltyCampaignSkuPriceFragment on LoyaltyCampaignSkuPrice {
    sku
    priceUsd
  }
`;

export const GET_LOYALTY_CAMPAIGN_ADMIN = gql`
  query getLoyaltyCampaignAdmin {
    liveOps {
      id
      loyaltyCampaignConfigs {
        ...LoyaltyCampaignConfigFragment
      }
      loyaltyCampaignCards {
        ...LoyaltyCampaignCardFragment
      }
    }
  }
  ${LOYALTY_CAMPAIGN_CONFIG_FRAGMENT}
  ${LOYALTY_CAMPAIGN_CARD_FRAGMENT}
`;

export const GET_LOYALTY_CAMPAIGN_SKU_OPTIONS = gql`
  query getLoyaltyCampaignSkuOptions {
    liveOps {
      id
      loyaltyCampaignOfferOptions {
        ...LoyaltyCampaignOfferOptionFragment
      }
      loyaltyCampaignSkuOptions
      loyaltyCampaignSkuPrices {
        ...LoyaltyCampaignSkuPriceFragment
      }
    }
  }
  ${LOYALTY_CAMPAIGN_OFFER_OPTION_FRAGMENT}
  ${LOYALTY_CAMPAIGN_SKU_PRICE_FRAGMENT}
`;

export const UPSERT_LOYALTY_CAMPAIGN_CONFIG = gql`
  mutation upsertLoyaltyCampaignConfig($id: ID!, $amount: Int!) {
    upsertLoyaltyCampaignConfig(id: $id, amount: $amount) {
      ...LoyaltyCampaignConfigFragment
    }
  }
  ${LOYALTY_CAMPAIGN_CONFIG_FRAGMENT}
`;

export const DELETE_LOYALTY_CAMPAIGN_CONFIG = gql`
  mutation deleteLoyaltyCampaignConfig($id: ID!) {
    deleteLoyaltyCampaignConfig(id: $id)
  }
`;

export const CREATE_LOYALTY_CAMPAIGN_CARD = gql`
  mutation createLoyaltyCampaignCard(
    $kind: LoyaltyCampaignCardKind!
    $name: String!
    $groupId: String
    $groupName: String
    $groupDisplayOrder: Int
    $groupStartDate: Date
    $groupEndDate: Date
    $subgroupId: String
    $subgroupName: String
    $subgroupDisplayOrder: Int
    $subgroupStartDate: Date
    $subgroupEndDate: Date
    $itemDisplayOrder: Int
    $cost: Int!
    $itemBonusLoyaltyPoints: Int
    $maxClaims: Int!
    $rewards: [LevelRewardInput!]!
  ) {
    createLoyaltyCampaignCard(
      kind: $kind
      name: $name
      groupId: $groupId
      groupName: $groupName
      groupDisplayOrder: $groupDisplayOrder
      groupStartDate: $groupStartDate
      groupEndDate: $groupEndDate
      subgroupId: $subgroupId
      subgroupName: $subgroupName
      subgroupDisplayOrder: $subgroupDisplayOrder
      subgroupStartDate: $subgroupStartDate
      subgroupEndDate: $subgroupEndDate
      itemDisplayOrder: $itemDisplayOrder
      cost: $cost
      itemBonusLoyaltyPoints: $itemBonusLoyaltyPoints
      maxClaims: $maxClaims
      rewards: $rewards
    ) {
      ...LoyaltyCampaignCardFragment
    }
  }
  ${LOYALTY_CAMPAIGN_CARD_FRAGMENT}
`;

export const UPDATE_LOYALTY_CAMPAIGN_CARD = gql`
  mutation updateLoyaltyCampaignCard(
    $id: ID!
    $kind: LoyaltyCampaignCardKind
    $name: String
    $groupId: String
    $groupName: String
    $groupDisplayOrder: Int
    $groupStartDate: Date
    $groupEndDate: Date
    $subgroupId: String
    $subgroupName: String
    $subgroupDisplayOrder: Int
    $subgroupStartDate: Date
    $subgroupEndDate: Date
    $itemDisplayOrder: Int
    $cost: Int
    $itemBonusLoyaltyPoints: Int
    $displayOrder: Int
    $maxClaims: Int
    $rewards: [LevelRewardInput!]
  ) {
    updateLoyaltyCampaignCard(
      id: $id
      kind: $kind
      name: $name
      groupId: $groupId
      groupName: $groupName
      groupDisplayOrder: $groupDisplayOrder
      groupStartDate: $groupStartDate
      groupEndDate: $groupEndDate
      subgroupId: $subgroupId
      subgroupName: $subgroupName
      subgroupDisplayOrder: $subgroupDisplayOrder
      subgroupStartDate: $subgroupStartDate
      subgroupEndDate: $subgroupEndDate
      itemDisplayOrder: $itemDisplayOrder
      cost: $cost
      itemBonusLoyaltyPoints: $itemBonusLoyaltyPoints
      displayOrder: $displayOrder
      maxClaims: $maxClaims
      rewards: $rewards
    ) {
      ...LoyaltyCampaignCardFragment
    }
  }
  ${LOYALTY_CAMPAIGN_CARD_FRAGMENT}
`;

export const DELETE_LOYALTY_CAMPAIGN_CARD = gql`
  mutation deleteLoyaltyCampaignCard($id: ID!) {
    deleteLoyaltyCampaignCard(id: $id)
  }
`;

export const DELETE_LOYALTY_CAMPAIGN_GROUP = gql`
  mutation deleteLoyaltyCampaignGroup($groupId: String!) {
    deleteLoyaltyCampaignGroup(groupId: $groupId)
  }
`;

export const DELETE_LOYALTY_CAMPAIGN_SUBGROUP = gql`
  mutation deleteLoyaltyCampaignSubgroup($groupId: String!, $subgroupId: String!) {
    deleteLoyaltyCampaignSubgroup(groupId: $groupId, subgroupId: $subgroupId)
  }
`;

export const UPDATE_LOYALTY_CAMPAIGN_GROUP = gql`
  mutation updateLoyaltyCampaignGroup(
    $groupId: String!
    $groupName: String!
    $groupDisplayOrder: Int
    $startDate: Date
    $endDate: Date
  ) {
    updateLoyaltyCampaignGroup(
      groupId: $groupId
      groupName: $groupName
      groupDisplayOrder: $groupDisplayOrder
      startDate: $startDate
      endDate: $endDate
    )
  }
`;

export const UPDATE_LOYALTY_CAMPAIGN_SUBGROUP = gql`
  mutation updateLoyaltyCampaignSubgroup(
    $groupId: String!
    $subgroupId: String!
    $subgroupName: String!
    $subgroupDisplayOrder: Int
    $subgroupStartDate: Date
    $subgroupEndDate: Date
  ) {
    updateLoyaltyCampaignSubgroup(
      groupId: $groupId
      subgroupId: $subgroupId
      subgroupName: $subgroupName
      subgroupDisplayOrder: $subgroupDisplayOrder
      subgroupStartDate: $subgroupStartDate
      subgroupEndDate: $subgroupEndDate
    )
  }
`;
